// Declarative control-pane binding. Collapses the repeated
// `folder.addBinding(target, key, opts).on('change', cb)` (and its slider-tick
// variant) into one descriptor, so a pane's controls can be expressed as data —
// the shape webpic's schema-aware `bindControl` inherits.
//
// `canonicalName` is a reserved seam for that future: once a canonical field
// registry exists (webpic's `@webpic/schema/registry`), label/units/format/range
// will resolve from it. magviz has no per-canonical-field registry today
// (`viewer.field.metadata` describes only the loaded field), so the binder
// honours just the explicit fields below — no resolution logic is wired yet.

import { bindSlider } from './sliderTicks';
import type { TBindingLike, TFolderLike } from './types';

export interface ControlDescriptor {
    /** Key on the bound target (a params key). */
    key: string;
    /** Reserved webpic seam — a canonical field name (`'|B|'`, `'beta'`).
     *  Not consulted today; the registry that resolves it ships with webpic. */
    canonicalName?: string;
    label?: string;
    min?: number;
    max?: number;
    step?: number;
    /** pane number-format fn (see ./numberFormat). */
    format?: (v: number) => string;
    /** Dropdown options — presence selects an enum control. */
    options?: Record<string, unknown>;
    /** Non-linear position↔value mapping (custom slider only). */
    scale?: 'linear' | 'log' | 'symlog';
    /** symlog linear half-width around 0 (custom slider only). */
    linthresh?: number;
    /** Single-mode fill anchor for the custom slider — e.g. 0 for a bipolar field. */
    origin?: number;
    /** Change handler, receiving the new value. */
    onChange?: (value: unknown) => void;
}

/** Bind one control from a descriptor. A numeric min/max/step triple routes
 *  through `bindSlider` for tick decoration; anything else is a plain
 *  `addBinding`. Returns the binding so callers can chain if needed. */
export function bindControl(folder: TFolderLike, target: Record<string, unknown>, desc: ControlDescriptor): TBindingLike {
    const opts: Record<string, unknown> = {};
    if (desc.label !== undefined) opts['label'] = desc.label;
    if (desc.min !== undefined) opts['min'] = desc.min;
    if (desc.max !== undefined) opts['max'] = desc.max;
    if (desc.step !== undefined) opts['step'] = desc.step;
    if (desc.format !== undefined) opts['format'] = desc.format;
    if (desc.options !== undefined) opts['options'] = desc.options;
    if (desc.scale !== undefined) opts['scale'] = desc.scale;
    if (desc.linthresh !== undefined) opts['linthresh'] = desc.linthresh;
    if (desc.origin !== undefined) opts['origin'] = desc.origin;

    const isSlider = typeof desc.min === 'number' && typeof desc.max === 'number' && typeof desc.step === 'number';
    const binding = isSlider
        ? bindSlider(folder, target, desc.key, opts)
        : folder.addBinding(target, desc.key, opts);
    if (desc.onChange) {
        const cb = desc.onChange;
        binding.on('change', (e) => cb(e.value));
    }
    return binding;
}

/** Bind a list of descriptors in declaration order. */
export function bindControls(folder: TFolderLike, target: Record<string, unknown>, descs: ReadonlyArray<ControlDescriptor>): void {
    for (const desc of descs) bindControl(folder, target, desc);
}
